import * as Sentry from "@sentry/nextjs"
import {
  Alert,
  Button,
  Checkbox,
  Label,
  Modal,
  Radio,
  Spinner,
  TextInput,
  Tooltip,
} from "flowbite-react"
import { Form, Formik, FormikHelpers, FormikProps } from "formik"
import moment from "moment"
import Link from "next/link"
import { useRouter } from "next/router"
import React, { useEffect, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import * as Yup from "yup"

import {
  AddModeIcon,
  BillIcon,
  CashIcon,
  CheckoutPlus,
  CloseCircleIcon,
  CreditIcon,
  ShoppingCartIcon,
  TimeIcon,
} from "@assets/icons"
import CustomImage from "@components/elements/CustomImage"
import FormGroup from "@components/elements/FormGroup"
import useAuth from "@hooks/useAuth"
import useConfirm from "@hooks/useConfirm"
import { useMediaQuery } from "@hooks/useMediaQuery"
import useToast from "@hooks/useToast"
import BookingSwLogo from "@icons/BookingSwLogo"
import CalendarIcon from "@icons/Calendar"
import CautionIcon from "@icons/Caution"
import EditUserIcon from "@icons/EditUser"
import EmailIcon from "@icons/Email"
import GiftIcon from "@icons/Gift"
import InfoIcon from "@icons/Info"
import PhoneIcon from "@icons/Phone"
import UserIcon from "@icons/User"
import ValidPromoCodeIcon from "@icons/ValidPromoCode"
import { CartItemDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal"
import SquareCreditCard from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/SquareCreditCardForm"
import { SquareCreditCardFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/SquareCreditCardForm/SquareCreditCardForm"
import StripeCreditCard from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/StripeCreditCardForm"
import { StripeCreditCardFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PaymentModal/StripeCreditCardForm/StripeCreditCardForm"
import PreviewTip from "@modules/tenants/SettingsPage/PaymentSettings/Components/PreviewTip"
import { domainPlaceholder, widgetsDomainName } from "@mono/essentials/constants/options"
import routes from "@mono/essentials/constants/routes"
import * as CustomerServices from "@mono/essentials/services/CustomerService"
import * as IntegrationServices from "@mono/essentials/services/IntegrationServices"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { UserChannel } from "@mono/essentials/types/AuthServicesTypes"
import { CreateCartRequestType, PaymentTypes } from "@mono/essentials/types/CartTypes"
import { UpdateParticipantsInCartPayloadType } from "@mono/essentials/types/CartTypes"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"
import {
  ApplyMembershipCodeType,
  CreateOrderRequestType,
  GetOrderDetailsResponseType,
} from "@mono/essentials/types/OrderTypes"
import { ProductCategoryTypes } from "@mono/essentials/types/OrderTypes"
import { IDomain } from "@mono/essentials/types/UserTypes"
import {
  capitalizeFirstLetter,
  getDateInDomainTimezone,
  phpToMoment,
} from "@mono/essentials/utils/Common"
import { getDayDifference } from "@mono/essentials/utils/Common"

import EditCustomerModal from "../EditCustomerModal"
import PaymentConfirmationModal from "../PaymentConfirmationModal"
import SelectProductModal from "../SelectproductModal"
import VerifyMembershipForm from "../VerifyMembershipForm"

import PaymentSection from "./PaymentSection"
import Divider, { DividerTypeEnum } from "@components/elements/Divider"

interface CheckoutProps {
  show?: boolean
  handleModalClose?: () => void
  goToBackStep?: () => void
  cartId: string
  onsucess?: (orderData?: GetOrderDetailsResponseType) => void
  isIntegratedPage: boolean
  domainDetails?: IDomain
  hideCustomerDetails?: boolean
}

const Checkout = ({
  show,
  handleModalClose,
  goToBackStep,
  cartId,
  onsucess,
  isIntegratedPage,
  domainDetails,
  hideCustomerDetails = false,
}: CheckoutProps) => {
  const confirm = useConfirm()
  const { isCustomerLoggedIn } = useAuth()
  const { showToast } = useToast()
  const formikRef = useRef<FormikProps<any>>(null)
  const [isPromoCodeValidated, setIsPromocodeValidated] = useState(false)
  const [isAgentCodeValidated, setIsAgentcodeValidated] = useState(false)
  const [isOverlayLoader, setIsOverlayLoader] = useState(false)
  const [error, setError] = useState("")
  const [showProductModal, setShowProductModal] = useState(false)
  const [editUserModal, setEditUserModal] = useState(false)
  const router = useRouter()
  const stripeCreditCardFormRef = useRef<FormikProps<StripeCreditCardFormAttributes>>(null)
  const squareUpFormRef = useRef<FormikProps<SquareCreditCardFormAttributes>>(null)
  const sqaureUpRef = useRef<any>(null)
  const [giftcardDetails, setGiftcardDetails] = useState<any["giftcard"]>()
  const [membershipLoading, setMembershipLoading] = useState(false)
  const isMobile = useMediaQuery(768)
  const [isTipSubmitting, setIsTipSubmitting] = useState(false)
  const membershipFormRef = useRef<any>(null)

  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(true)
  const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false)

  const { data: integrationsData } = useSWR(
    "intergrationsData",
    async () => {
      try {
        const { data: getCartResponse } = await IntegrationServices.getIntegrations(
          {
            type: "payment",
          },
          isIntegratedPage
        )
        if (getCartResponse) {
          return getCartResponse
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
    }
  )

  const {
    data: cartData,
    isLoading: cartDataLoading,
    isValidating: overlayLoader,
    mutate: refetchCartData,
  } = useSWR(
    cartId ? `cartData-${cartId}` : null,
    async () => {
      try {
        const { data: getCartResponse } = await OrderServices.getCart(cartId, isIntegratedPage)
        if (getCartResponse) {
          return getCartResponse.data
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      } finally {
        setIsOverlayLoader(false)
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const initialValues: any = useMemo(() => {
    return {
      paymentType: cartData?.is_deposit_enabled_for_items
        ? PaymentTypes.DEPOSIT_AMOUNT
        : PaymentTypes.FULL_AMOUNT,
      amount: `${
        cartData?.is_deposit_enabled_for_items
          ? cartData?.deposit_pay_now?.amount_in_units
          : (cartData?.full_amount_pay_now?.amount_in_units ?? cartData?.total_with_tip ?? 0)
      }`,
      code: "",
      name: "",
      activeTab: 0,
      agreeTermsAndConditions: true,
      showAgentCode: false,
      agentCode: "",
      showPromoCode: false,
      promoCode: "",
    }
  }, [cartData])

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        paymentType: Yup.string().required("*Payment Type is Required"),
        amount: Yup.number().required("*Amount is Required"),
        code: Yup.string().when("code_type", (activeTab, schema) =>
          activeTab[0] === 1 ? schema.nullable().required("*Code is Required") : schema.nullable()
        ),
        name: Yup.string().when("code_type", (activeTab, schema) =>
          activeTab[0] === 2
            ? schema.nullable().required("*Card Name is Required")
            : schema.nullable()
        ),
      }),
    []
  )

  useEffect(() => {
    if (cartData && cartData?.offer_code) {
      setIsPromocodeValidated(true)
      formikRef?.current?.setFieldValue("showPromoCode", true)
      formikRef?.current?.setFieldValue("promoCode", cartData?.offer_code)
    }
    if (cartData && cartData?.affiliate?.affiliate_code) {
      formikRef?.current?.setFieldValue("showAgentCode", true)
      formikRef?.current?.setFieldValue("agentCode", cartData?.affiliate?.affiliate_code)
      setIsAgentcodeValidated(true)
    }
  }, [cartData])

  const integratedData = useMemo(() => {
    return (integrationsData || [])?.filter(
      (integration) =>
        (integration.name === "Stripe" || integration.name === "SquareUp") &&
        integration.is_installed &&
        integration.status === "active"
    )
  }, [integrationsData, cartData])

  const PAYMENT_TYPES: SelectOptionType[] = useMemo(
    () => [
      {
        label: "Full Amount",
        value: PaymentTypes.FULL_AMOUNT,
      },
      {
        label: "Deposit Amount",
        value: PaymentTypes.DEPOSIT_AMOUNT,
      },
    ],
    []
  )

  const showPendingBookingPaymentConfirmation = (modalStatus: boolean) => {
    setShowPaymentConfirmationModal(modalStatus)
  }

  const handlePaymentError = () => {
    showPendingBookingPaymentConfirmation(false)
    setShowPaymentConfirmation(true)
    formikRef?.current?.setFieldValue("isLoading", false)
  }

  const {
    data: participantsDetails,
    isLoading: isParticipantsDetailsLoading,
    mutate: refetchParticipantsDetails,
  } = useSWR(
    router.isReady ? "participantsDetails" : null,
    async () => {
      try {
        const { data: getParticipantsDetailsResponse } =
          await OrderServices.getParticipantsFromCart(cartId, isIntegratedPage)
        if (getParticipantsDetailsResponse) {
          return getParticipantsDetailsResponse.data
        }
      } catch (e) {
        Sentry.captureException(e)
        console.error(e.response.data.message)
        showToast({ heading: e.response.data.message, type: "danger" })
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const handleApplyPromoCode = async (promocode: string, paymentType?: string) => {
    try {
      if (cartData?.id && promocode) {
        setIsOverlayLoader?.(true)
        const applyPromocodeResponse = await OrderServices.applyPromocode(
          cartData.id,
          {
            offer_code: promocode,
            payment_type: paymentType,
          },
          isIntegratedPage
        )
        if (applyPromocodeResponse.data.code === 200) {
          if (applyPromocodeResponse?.data?.giftcard) {
            setGiftcardDetails({
              ...applyPromocodeResponse.data.giftcard,
              code: promocode,
            })
            if (paymentType === PaymentTypes.DEPOSIT_AMOUNT) {
              formikRef?.current?.setFieldValue?.(
                "amount",
                applyPromocodeResponse.data.giftcard?.remaining_deposit_total?.amount_in_units || 0
              )
            } else {
              formikRef?.current?.setFieldValue?.(
                "amount",
                applyPromocodeResponse.data.giftcard?.remaining_total?.amount_in_units || 0
              )
            }
          }
          await refetchCartData()
          setIsPromocodeValidated(true)
        } else {
          formikRef?.current?.setFieldError?.(
            "promoCode",
            applyPromocodeResponse?.data?.message ??
              "The code you have entered isn't valid. Please recheck code."
          )
          setIsOverlayLoader?.(false)
        }
      }
    } catch (error: any) {
      Sentry.captureException(error)
      formikRef?.current?.setFieldError?.(
        "promoCode",
        "The code you have entered isn't valid. Please recheck code."
      )
    } finally {
      setIsOverlayLoader?.(false)
    }
  }

  const handleApplyAgentCode = async (
    agentCode?: string,
    setFieldError?: (field: string, message: string | undefined) => void
  ) => {
    try {
      setIsOverlayLoader?.(true)
      const headers = {}
      if (agentCode) {
        headers["X-Channel"] = UserChannel.AFFILIATE
      }
      await OrderServices.updateCart(
        cartId,
        {
          ...getCartUpdatePayload(),
          affiliate_id: agentCode,
        },
        isIntegratedPage,
        { headers }
      )
      await refetchCartData()
      setIsAgentcodeValidated(true)
    } catch (e) {
      Sentry.captureException(e)
      setFieldError?.("agentCode", "The code you have entered isn't valid. Please recheck code.")
      console.error("Update affiliate error: ", e)
    } finally {
      setIsOverlayLoader?.(false)
    }
  }

  const handleCreateOrder = async (
    paymentMethodId: string | number,
    paymentType: PaymentTypes,
    paymentToken?: string,
    amount?: number,
    nameOnCard?: string
  ) => {
    try {
      formikRef?.current?.setFieldValue("isLoading", true)
      const payload: CreateOrderRequestType = {
        cart_id: cartId,
        payment_method: `${paymentMethodId}`,
        payment_type: paymentType,
      }
      if (paymentToken) payload.payment_token = paymentToken
      if (typeof amount !== "undefined") payload.amount = amount
      if (nameOnCard) payload.name_on_card = nameOnCard
      if (Boolean(giftcardDetails) && giftcardDetails?.code)
        payload.gift_card_code = giftcardDetails.code
      const createOrderResponse = await OrderServices.createOrder(payload, isIntegratedPage)
      if (createOrderResponse) {
        onsucess?.(createOrderResponse.data.order)
        formikRef?.current?.resetForm()
      }
    } catch (e: any) {
      Sentry.captureException(e)
      console.error(e.response.data.message)
      showToast({ heading: e.response.data.message, type: "danger" })
      if (e?.response?.data?.message) {
        setError(e?.response?.data?.message)
      }
    } finally {
      formikRef?.current?.setFieldValue("isLoading", false)
    }
  }

  const handlePayment = (values: any, setFieldValue: any) => {
    try {
      const integrationName = integratedData && integratedData[values.activeTab].name
      if (
        Boolean(giftcardDetails) && values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
          ? giftcardDetails?.remaining_deposit_total.amount === 0
          : giftcardDetails?.remaining_total.amount === 0
      ) {
        handleCreateOrder(
          integrationsData.find((integration) => integration.name === "Gift Card")?.id,
          values.paymentType,
          undefined,
          values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
            ? cartData?.deposit_pay_now?.amount_in_units
            : (cartData?.full_amount_pay_now?.amount_in_units ?? cartData?.total_with_tip)
        )
      } else {
        if (integrationName === "Stripe") {
          stripeCreditCardFormRef?.current?.submitForm()
          if (stripeCreditCardFormRef?.current?.values?.hasPaymentValidData) {
            setFieldValue("isLoading", true)
          }
        } else if (integrationName === "SquareUp") {
          if (
            Boolean(cartData?.customer?.is_member) &&
            squareUpFormRef?.current?.values?.isUsingSavedCard &&
            Boolean(squareUpFormRef?.current?.values.savedCardId)
          ) {
            squareUpFormRef?.current?.submitForm()
          } else {
            setFieldValue("isLoading", true)
            const payButton = sqaureUpRef?.current?.querySelector("#rswp-card-button")
            if (payButton) {
              payButton.click()
            }
          }
        }
      }
    } catch (error) {
      Sentry.captureException(error)
      setFieldValue("isLoading", false)
    }
  }

  const onSubmit = async (values: any, { setFieldValue }: FormikHelpers<any>) => {
    try {
      setError("")
      const fullPaymentTypeOption = giftcardDetails
        ? giftcardDetails.remaining_total.amount === 0 &&
          cartData?.full_amount_pay_later.amount_in_units === 0 &&
          Number(values?.amount) === 0
        : cartData?.deposit_pay_later.amount_in_units === 0 &&
          cartData?.full_amount_pay_later.amount_in_units === 0 &&
          Number(values?.amount) === 0

      const depositPaymentTypeOption = giftcardDetails
        ? giftcardDetails.remaining_deposit_total.amount === 0 &&
          !cartData?.has_pending_booking &&
          cartData?.is_deposit_enabled_for_items &&
          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
        : !cartData?.has_pending_booking &&
          cartData?.deposit_pay_now?.amount_in_units === 0 &&
          cartData?.deposit === 0 &&
          !(cartData?.deposit_with_tip > 0) &&
          cartData?.is_deposit_enabled_for_items &&
          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT

      const depositPaymentTypeOptionForZeroDeposit =
        cartData?.has_pending_booking &&
        cartData?.deposit_pay_now?.amount_in_units === 0 &&
        cartData?.is_deposit_enabled_for_items &&
        values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT &&
        cartData?.full_amount_pay_later?.amount_in_units === 0

      if (
        fullPaymentTypeOption ||
        depositPaymentTypeOption ||
        depositPaymentTypeOptionForZeroDeposit
      ) {
        const payload: CreateOrderRequestType = {
          cart_id: cartData?.id,
          payment_type:
            fullPaymentTypeOption && values.paymentType === PaymentTypes.FULL_AMOUNT
              ? PaymentTypes.FULL_AMOUNT
              : PaymentTypes.DEPOSIT_AMOUNT,
        }
        if (giftcardDetails) {
          payload.gift_card_code = giftcardDetails?.code
          payload.payment_method = integrationsData?.find(
            (integration) => integration.name === "Gift Card"
          )?.id
        }
        const createOrderResponse = await OrderServices.createOrder(payload, isIntegratedPage)
        if (createOrderResponse) {
          if (isIntegratedPage) {
            onsucess?.(createOrderResponse.data.order)
          } else {
            onsucess?.()
            showToast({
              heading: (
                <div className={"flex flex-row items-center justify-between"}>
                  Payment successful!
                </div>
              ),
            })
          }
          formikRef?.current?.resetForm()
        }
      } else {
        handlePayment(values, setFieldValue)
      }
    } catch (e: any) {
      Sentry.captureException(e)
      console.error(e.response.data.message)
      showToast({ heading: e.response.data.message, type: "danger" })
      setFieldValue("isLoading", false)
    }
  }

  const getCartUpdatePayload: () => CreateCartRequestType = () => {
    if (cartData) {
      return {
        customer_id: cartData?.customer.id,
        affiliate_id: cartData?.affiliate?.id,
        items: cartData.items.map((cartItem) => ({
          cart_item_id: cartItem?.id,
          product_id: cartItem?.product.id,
          slot_id: cartItem?.slot_id ?? "",
          quantities: cartItem.quantities.map((participantGroup) => ({
            unit_id: participantGroup.unit?.id,
            quantity: participantGroup.quantity,
            start_at:
              moment(cartItem?.product_availability_slot?.start_at).format(
                phpToMoment("Y-m-d H:i:s")
              ) ?? "",
            end_at:
              moment(cartItem?.product_availability_slot?.end_at).format(
                phpToMoment("Y-m-d H:i:s")
              ) ?? "",
            availability_id: cartItem?.product_availability_slot?.product_availability_id ?? "",
            amount: participantGroup?.display_price,
          })),
        })),
        tips: cartData.tips.map((tip) => ({
          type: tip.type,
          value: tip.value,
          source: tip.source,
        })),
      }
    }
    return undefined
  }

  const getCartItemsParticipantsPayload = () => {
    const finalPayload: Record<string, UpdateParticipantsInCartPayloadType[]> = {}
    cartData.items
      .filter((item) => item?.product?.type?.internal_type !== ProductCategoryTypes.ADD_ONS)
      .forEach((cartItem) => {
        const payload: UpdateParticipantsInCartPayloadType[] = []
        participantsDetails
          .filter((cartItemFields) => cartItemFields.cart_item_id === cartItem.id)
          .map((cartItemUnitParticaipantFields) => {
            payload.push({
              unit_id: cartItemUnitParticaipantFields.unit.id,
              fields: cartItemUnitParticaipantFields.fields.map((participantField) => ({
                product_field_id: participantField.id,
                value: participantField.value,
              })),
            })
          })
        if (payload.length) finalPayload[cartItem.id] = payload
      })
    return finalPayload
  }

  const handleShowMultiDay = (startDate, endDate) => {
    const start_Date = moment(startDate)
    const end_date = moment(endDate)

    const dayDifference = getDayDifference(start_Date, end_date) + 1

    return `${dayDifference} Day${dayDifference > 1 ? "s" : ""} `
  }

  const onMembershipChange = async (membershipCode, action, callback = null) => {
    try {
      setIsOverlayLoader(true)
      if (action === "apply") {
        await OrderServices.applyMembershipToCart(
          cartData?.id,
          {
            code: membershipCode,
            code_type: ApplyMembershipCodeType.MEMBERSHIP_CODE,
          },
          isIntegratedPage
        )
      } else if (action === "remove") {
        await OrderServices.removeMembershipFromCart(cartData?.id, isIntegratedPage)
      }
      await refetchCartData()
    } catch (e) {
      if (callback) {
        callback?.(false)
      }
      Sentry.captureException(e)
      showToast({ heading: e?.response?.data?.message || "Something went wrong!", type: "danger" })
    } finally {
      setIsOverlayLoader?.(false)
    }
  }

  const { data: activeMembershipList = [], mutate: refetchCustomerMemberships } = useSWR(
    cartData?.customer?.is_member
      ? `widgets-customerMembershipList-${cartData?.customer?.id}`
      : null,
    async () => {
      const { data: getAllCustomerMembershipsResponse } =
        await CustomerServices.getAllCustomerMemberships(
          cartData?.customer?.id,
          {
            includeMembershipDetails: 1,
          },
          isIntegratedPage
        )
      if (getAllCustomerMembershipsResponse) {
        const membershipList = getAllCustomerMembershipsResponse?.data?.filter((membership) =>
          Boolean(membership.is_active)
        )
        return membershipList
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const getPendingConfirmProducts = useMemo(() => {
    if (cartData?.items?.length) {
      return cartData?.items
        ?.filter((item: any) => item.product.auto_confirm_booking === false)
        ?.map((cartItem) => cartItem?.product?.name)
    }
    return []
  }, [cartData])

  const loaderOverlay = overlayLoader || isTipSubmitting || isOverlayLoader

  const showPaymentOptions = useMemo(() => {
    return (
      domainDetails?.enable_deposit_option &&
      cartData?.is_deposit_enabled_for_items &&
      cartData?.has_confirm_booking &&
      (Boolean(cartData?.total) ||
        (giftcardDetails &&
          (giftcardDetails?.remaining_deposit_total?.amount > 0 ||
            giftcardDetails?.remaining_total?.amount > 0)))
    )
  }, [cartData, domainDetails])

  return (
    <>
      <Modal
        show={show}
        onClose={handleModalClose}
        size={"6xl"}
        theme={{
          content: {
            base: `relative w-full h-auto rounded-lg max-h-[92vh] overflow-hidden mx-2 !max-w-[1180px] `,
          },
        }}
        className={"widget-theme-wrapper"}
      >
        <Modal.Header
          theme={{
            title: "w-full sm:text16Medium text12Medium text-neutral-900 dark:text-white",
          }}
          className={"px-2 sm:px-6"}
        >
          <div className={"flex items-center justify-between"}>
            <div className={"flex items-center gap-2"}>
              {domainDetails?.logo ? (
                <>
                  <CustomImage
                    src={domainDetails?.logo}
                    className={"w-auto object-cover h-7"}
                    height={20}
                    width={40}
                    fill={false}
                  />
                  <Divider
                    type={DividerTypeEnum.vertical}
                    className="border-neutral-300 !w-[1px]"
                  />
                </>
              ) : null}
              {/* <LockIcon className="sm:mr-2 mr-1 h-4.5 w-4.5" /> */}
              {/* <LeftSmallIcon width={18} height={18} className="mx-2 cursor-pointer text-primary-600" /> */}
              <span className={"text14Semibold"}>Confirm & Checkout</span>
            </div>
            <div
              className={
                "sm:mr-2 mr-1 flex items-center gap-2 rounded bg-primary-100 sm:px-6 px-2.5 py-2.5 sm:py-3"
              }
            >
              <ShoppingCartIcon className={"text-primary-600"} />
              {cartData?.items.length}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className={"!bg-white"}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            innerRef={formikRef}
            enableReinitialize
            onSubmit={onSubmit}
          >
            {({
              values,
              getFieldProps,
              setFieldValue,
              errors,
              touched,
              setFieldError,
              setFieldTouched,
              isSubmitting,
              resetForm,
              handleSubmit,
            }) => {
              const hasPendingBooking = Boolean(cartData?.has_pending_booking)
              const fullAmountPayNow =
                Boolean(cartData?.full_amount_pay_now?.amount_in_units) ||
                Boolean(cartData?.full_amount_pay_later?.amount_in_units) ||
                Boolean(cartData?.deposit_pay_later?.amount_in_units)
              const depositPayNow = Boolean(cartData?.deposit_pay_now?.amount_in_units)
              const totalWithTip = cartData?.total_with_tip > 0
              const depositWithTip = cartData?.deposit_with_tip > 0
              const isDepositEnabledForItems = cartData?.is_deposit_enabled_for_items
              const hasDeposit = Boolean(cartData?.deposit)
              const isFullAmount = values.paymentType === PaymentTypes.FULL_AMOUNT
              const isDepositAmount = values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
              const isDepositDisabledForItems = !isDepositEnabledForItems
              const isBothProducts = cartData?.has_confirm_booking && hasPendingBooking
              const isPlaceOrder =
                (cartData?.total_with_tip === 0 ||
                  cartData?.full_amount_pay_now?.amount_in_units === 0 ||
                  (cartData?.is_deposit_enabled_for_items &&
                    values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT &&
                    !(cartData?.deposit_with_tip > 0))) &&
                (Number(values?.amount) === 0 || cartData?.deposit === 0) &&
                cartData?.has_confirm_booking

              const showPaymentMethodCards = hasPendingBooking
                ? fullAmountPayNow || depositPayNow
                : (isFullAmount && totalWithTip) ||
                  (isDepositDisabledForItems && isDepositAmount && totalWithTip) ||
                  hasDeposit ||
                  (isDepositEnabledForItems && depositWithTip)
              return (
                <Form autoComplete={"off"}>
                  <div className={"relative"}>
                    {cartDataLoading ? (
                      <div className={"flex min-h-[350px] items-center justify-center"}>
                        <div className={"text-center"}>
                          <Spinner size={"md"} light className={"text-primary-400 fill-white"} />
                        </div>
                      </div>
                    ) : (
                      <>
                        {loaderOverlay && (
                          <div
                            className={
                              "absolute inset-0 bg-opacity-70 flex justify-center items-center z-50"
                            }
                          >
                            <Spinner />
                          </div>
                        )}
                        <div
                          className={`lg:p-8 md:p-6 sm:p-4 p-2 ${loaderOverlay ? "opacity-30" : "opacity-100"} transition-opacity duration-500`}
                        >
                          {integratedData?.length === 0 &&
                            !isPlaceOrder &&
                            (cartData?.total_with_tip > 0 ||
                              Boolean(cartData?.deposit_auto_confirm)) && (
                              <div className={"pb-5"}>
                                <Alert color={"failure"}>
                                  <span>Please install payment method for completing checkout</span>
                                </Alert>
                              </div>
                            )}
                          {error && (
                            <div className={"pb-5"}>
                              <Alert color={"failure"}>
                                <span>{error}</span>
                              </Alert>
                            </div>
                          )}
                          <div className={"md:flex"}>
                            <div
                              className={
                                "w-full md:mb-0 mb-3 lg:mr-8 md:mr-6 md:border-r border-neutral-200 lg:pr-8 md:pr-6"
                              }
                            >
                              <Button
                                color={"primary"}
                                onClick={() => setShowProductModal(true)}
                                className={`${integratedData.length > 0 ? "mb-5" : "mb-0"}`}
                              >
                                <CheckoutPlus className={"mr-1 h-5 w-5"} />
                                Add Another Product
                              </Button>
                              {domainDetails?.tip_status_in_widget === "active" ? (
                                <div className={"pb-6"}>
                                  <PreviewTip
                                    isWidgetsCard
                                    tipData={{
                                      tip_options: cartData?.subtotal
                                        ? domainDetails?.tip_options
                                        : [],
                                      tip_notes: domainDetails?.tip_notes,
                                      tip_type: cartData?.subtotal
                                        ? domainDetails?.tip_type
                                        : "fixed",
                                      is_custom_tip_allowed: cartData?.subtotal
                                        ? domainDetails?.is_custom_tip_allowed
                                        : true,
                                      tip_number: domainDetails?.tip_number,
                                    }}
                                    domainDetails={domainDetails}
                                    cartId={cartData?.id}
                                    cartDetails={
                                      getCartUpdatePayload() as unknown as CartItemDetailsType
                                    }
                                    setTipSubmitting={setIsTipSubmitting}
                                    tipInitialValues={
                                      cartData.tips.length
                                        ? {
                                            tipValue: cartData.tips[0].value,
                                            tipType: cartData.tips[0].type,
                                            totalTip: cartData.tips[0].formatted_amount,
                                          }
                                        : undefined
                                    }
                                    onSuccess={refetchCartData}
                                    isIntegratedPage={isIntegratedPage}
                                    paymentAmountDetails={{
                                      subTotalAmount: cartData.subtotal,
                                      totalAmount: cartData.total,
                                      totalAmountWithTip:
                                        cartData?.full_amount_pay_now?.amount_in_units,
                                    }}
                                  />
                                </div>
                              ) : null}
                              {cartData?.customer?.is_member &&
                              domainDetails?.enable_membership &&
                              isCustomerLoggedIn &&
                              activeMembershipList?.length > 0 ? (
                                <VerifyMembershipForm
                                  isIntegratedPage={isIntegratedPage}
                                  onMemberShipSelect={onMembershipChange}
                                  options={(activeMembershipList || []).map((opt) => {
                                    return {
                                      label: `${opt.code} ${opt.product.name} ${
                                        opt.end_at
                                          ? `Expires ${
                                              getDateInDomainTimezone(
                                                opt.end_at,
                                                domainDetails?.date_format
                                              ) ?? ""
                                            }`
                                          : ""
                                      }`,
                                      value: opt.code,
                                    }
                                  })}
                                  selectedMembershipId={cartData?.customerMembership?.code}
                                  onSuccessVerification={() => refetchCustomerMemberships()}
                                  showCustomerLogin={false}
                                  ref={membershipFormRef}
                                  autoApplyMembership={cartData?.auto_apply_membership}
                                />
                              ) : null}
                              <div
                                className={`${
                                  integratedData.length > 0 && !isMobile
                                    ? "shadow-sm border border-neutral-450 lg:pt-6 md:pt-5 sm:pt-4 pt-2 rounded-lg"
                                    : ""
                                }`}
                              >
                                {integratedData.length > 0 &&
                                  showPaymentMethodCards &&
                                  (giftcardDetails
                                    ? (values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                        ? giftcardDetails.remaining_deposit_total.amount
                                        : giftcardDetails.remaining_total.amount) > 0
                                    : true) && (
                                    <div
                                      className={`flex w-full flex-col items-start gap-4 ${`[&>div]:w-full`}`}
                                    >
                                      <PaymentSection
                                        integratedData={integratedData}
                                        cartData={cartData}
                                        stripeCreditCardFormRef={stripeCreditCardFormRef}
                                        handlePaymentError={handlePaymentError}
                                        cartId={cartId}
                                        isIntegratedPage={isIntegratedPage}
                                        onsucess={onsucess}
                                        giftcardDetails={giftcardDetails}
                                        sqaureUpRef={sqaureUpRef}
                                        squareUpFormRef={squareUpFormRef}
                                        handleCreateOrder={handleCreateOrder}
                                        showPendingBookingPaymentConfirmation={
                                          showPendingBookingPaymentConfirmation
                                        }
                                        handlePaymentConfirmation={
                                          showPaymentConfirmation && cartData?.has_pending_booking
                                        }
                                      />
                                    </div>
                                  )}
                                <div
                                  className={`hidden ${
                                    cartData.total_with_tip > 0 ? "mt-4" : ""
                                  } ${integratedData.length > 0 && !isMobile ? "lg:px-6 md:px-5 sm:px-4 px-2" : ""} md:flex `}
                                >
                                  <Checkbox
                                    id={"agreeTermsAndConditions"}
                                    checked={values.agreeTermsAndConditions}
                                    {...getFieldProps("agreeTermsAndConditions")}
                                    name={"agreeTermsAndConditions"}
                                    className={"cursor-pointer text-primary-600 mt-1"}
                                  />
                                  <Label
                                    htmlFor={"agreeTermsAndConditions"}
                                    className={"!paragraph14Regular px-2"}
                                  >
                                    I agree to all these&nbsp;
                                    <Link
                                      target={"_blank"}
                                      className={"cursor-pointer text-primary-600"}
                                      href={routes.TnC}
                                    >
                                      Terms & Conditions
                                    </Link>
                                    &nbsp;and your&nbsp;
                                    <Link
                                      target={"_blank"}
                                      className={"cursor-pointer text-primary-600"}
                                      href={routes.privacy}
                                    >
                                      Privacy Policy
                                    </Link>
                                    .
                                  </Label>
                                </div>
                                <div
                                  className={`lg:pb-6 md:pb-5 sm:pb-5 pb-2 ${integratedData.length > 0 && !isMobile ? "lg:px-6 md:px-5 sm:px-4 px-2" : ""}`}
                                >
                                  {showPaymentOptions ? (
                                    <div className={"md:block hidden mt-3"}>
                                      <div className={"paragraph14Medium text-neutral-900"}>
                                        Amount to pay now
                                      </div>
                                      <div
                                        className={
                                          "flex flex-row items-center justify-start gap-5 mt-2"
                                        }
                                      >
                                        {PAYMENT_TYPES.map((paymentType) => {
                                          return (
                                            <div
                                              className={"flex items-center gap-2"}
                                              key={paymentType.value}
                                            >
                                              <Radio
                                                id={`${paymentType.value}-1`}
                                                name={"paymentType-1"}
                                                value={paymentType.value}
                                                checked={Boolean(
                                                  values.paymentType === paymentType.value
                                                )}
                                                onChange={() => {
                                                  setFieldValue("paymentType", paymentType.value)
                                                  handleApplyPromoCode(
                                                    values.promoCode,
                                                    paymentType.value
                                                  )
                                                  if (
                                                    paymentType.value ===
                                                    PaymentTypes.DEPOSIT_AMOUNT
                                                  ) {
                                                    setFieldValue(
                                                      "amount",
                                                      cartData?.deposit_pay_now?.amount_in_units
                                                    )
                                                  } else if (
                                                    paymentType.value === PaymentTypes.FULL_AMOUNT
                                                  ) {
                                                    setFieldValue(
                                                      "amount",
                                                      cartData?.full_amount_pay_now
                                                        ?.amount_in_units ??
                                                        cartData?.total_with_tip
                                                    )
                                                  } else {
                                                    setFieldValue("amount", 0)
                                                  }

                                                  const integrationName =
                                                    integratedData &&
                                                    integratedData[values.activeTab]?.name
                                                  if (integrationName === "Stripe") {
                                                    if (
                                                      stripeCreditCardFormRef?.current
                                                        ?.setFieldValue
                                                    ) {
                                                      stripeCreditCardFormRef.current.setFieldValue(
                                                        "paymentType",
                                                        paymentType.value
                                                      )
                                                      if (
                                                        paymentType.value ===
                                                        PaymentTypes.DEPOSIT_AMOUNT
                                                      ) {
                                                        stripeCreditCardFormRef.current.setFieldValue(
                                                          "amount",
                                                          cartData?.deposit_pay_now?.amount_in_units
                                                        )
                                                      } else if (
                                                        paymentType.value ===
                                                        PaymentTypes.FULL_AMOUNT
                                                      ) {
                                                        stripeCreditCardFormRef.current.setFieldValue(
                                                          "amount",
                                                          cartData?.full_amount_pay_now
                                                            ?.amount_in_units ??
                                                            cartData?.total_with_tip
                                                        )
                                                      } else {
                                                        stripeCreditCardFormRef.current.setFieldValue(
                                                          "amount",
                                                          0
                                                        )
                                                      }
                                                    }
                                                  } else if (integrationName === "SquareUp") {
                                                    if (squareUpFormRef?.current?.setFieldValue) {
                                                      squareUpFormRef.current.setFieldValue(
                                                        "paymentType",
                                                        paymentType.value
                                                      )
                                                      if (
                                                        paymentType.value ===
                                                        PaymentTypes.DEPOSIT_AMOUNT
                                                      ) {
                                                        squareUpFormRef.current.setFieldValue(
                                                          "amount",
                                                          cartData?.deposit_pay_now?.amount_in_units
                                                        )
                                                      } else if (
                                                        paymentType.value ===
                                                        PaymentTypes.FULL_AMOUNT
                                                      ) {
                                                        squareUpFormRef.current.setFieldValue(
                                                          "amount",
                                                          cartData?.full_amount_pay_now
                                                            ?.amount_in_units ??
                                                            cartData?.total_with_tip
                                                        )
                                                      } else {
                                                        squareUpFormRef.current.setFieldValue(
                                                          "amount",
                                                          0
                                                        )
                                                      }
                                                    }
                                                  }
                                                }}
                                              />
                                              <Label
                                                htmlFor={`${paymentType.value}-1`}
                                                className={"paragraph14Regular text-neutral-900"}
                                              >
                                                {paymentType.label}
                                              </Label>
                                            </div>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  ) : null}
                                  <Button
                                    disabled={
                                      isSubmitting ||
                                      values.isLoading ||
                                      !values.agreeTermsAndConditions ||
                                      (integratedData.length === 0 &&
                                        !isPlaceOrder &&
                                        (cartData?.total_with_tip > 0 ||
                                          Boolean(cartData?.deposit_auto_confirm)))
                                    }
                                    type={"submit"}
                                    data-testid={"depositPayButton"}
                                    color={"primary"}
                                    className={
                                      "hidden w-full mt-4 md:block [&>span]:!text16Medium [&>span]:!py-[13px]"
                                    }
                                  >
                                    {isSubmitting || values.isLoading ? (
                                      <Spinner
                                        size={"sm"}
                                        light
                                        className={"text-primary-400 fill-white"}
                                      />
                                    ) : giftcardDetails ? (
                                      (values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                        ? giftcardDetails?.remaining_deposit_total.amount
                                        : giftcardDetails?.remaining_total.amount) === 0 ? (
                                        `Place order`
                                      ) : (
                                        `Pay ${
                                          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                            ? giftcardDetails?.remaining_deposit_total
                                                .amount_formatted
                                            : giftcardDetails?.remaining_total.amount_formatted
                                        }`
                                      )
                                    ) : (cartData?.total_with_tip === 0 ||
                                        cartData?.full_amount_pay_now?.amount_in_units === 0 ||
                                        (cartData?.is_deposit_enabled_for_items &&
                                          values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT &&
                                          !(cartData?.deposit_with_tip > 0))) &&
                                      (Number(values?.amount) === 0 || cartData?.deposit === 0) ? (
                                      "Place order"
                                    ) : (
                                      `Pay ${
                                        values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                          ? cartData?.deposit_pay_now?.amount_formatted
                                          : cartData?.full_amount_pay_now?.amount_formatted
                                      }`
                                    )}
                                  </Button>
                                </div>
                              </div>
                              {getPendingConfirmProducts?.length > 0 ? (
                                <div
                                  className={integratedData.length > 0 && !isMobile ? "mt-8" : ""}
                                >
                                  <div
                                    className={`
                                      text13Medium text-[14.5px] text-[#dc3838] bg-[#F8E5E5] px-3 py-2.5 rounded-lg
                                      animate-[pulse_2s_ease-in-out_5]
                                    `}
                                  >
                                    Your booking for{" "}
                                    <span
                                      className={"text-[#dc3838]"}
                                    >{`${getPendingConfirmProducts.join(",")}`}</span>{" "}
                                    is pending confirmation by the operator. You will receive an
                                    email notification once the booking is either confirmed or
                                    cancelled.
                                  </div>
                                </div>
                              ) : null}
                            </div>
                            <div className={"md:w-6/12 md:mb-0 mb-3"} data-testid={"cartSummary"}>
                              <div
                                className={
                                  "rounded-lg bg-white border border-neutral-450 shadow-sm md:pt-5 sm:pt-4 pt-3 lg:px-6 md:px-5 sm:px-4 px-3 lg:pb-8 md:pb-5 sm:pb-4 pb-3"
                                }
                              >
                                {cartData?.customer && (
                                  <div className={"flex justify-between w-full"}>
                                    <div className={"flex flex-col items-start gap-2"}>
                                      <div
                                        className={"flex flex-row items-center justify-start gap-2"}
                                      >
                                        <UserIcon className={"[&>path]:stroke-neutral-150"} />
                                        <span className={"text14Regular text-neutral-900"}>
                                          {`${cartData.customer.first_name} ${
                                            cartData.customer.last_name
                                          }`}
                                        </span>
                                      </div>
                                      <div
                                        className={"flex flex-row items-center justify-start gap-2"}
                                      >
                                        <EmailIcon className={"stroke-neutral-150"} />
                                        <span className={"text14Regular text-neutral-900"}>
                                          {cartData.customer.email}
                                        </span>
                                      </div>
                                      <div
                                        className={"flex flex-row items-center justify-start gap-2"}
                                      >
                                        <PhoneIcon className={"[&>path]:stroke-neutral-150"} />
                                        <span className={"text14Regular text-neutral-900"}>
                                          {`+${cartData.customer.phone_code} ${
                                            cartData.customer.phone
                                          }`}
                                        </span>
                                      </div>
                                    </div>
                                    <div>
                                      <Tooltip
                                        content={"Edit Customer Details"}
                                        trigger={"hover"}
                                        placement={"top-start"}
                                      >
                                        <EditUserIcon
                                          className={
                                            "p-1 rounded-full cursor-pointer h-7 w-7 bg-primary-100"
                                          }
                                          onClick={() => setEditUserModal(true)}
                                        />
                                      </Tooltip>
                                    </div>
                                  </div>
                                )}
                                <div
                                  className={"w-full my-3 border-t border-solid border-neutral-450"}
                                />
                                {cartData?.items.length && (
                                  <>
                                    {cartData.items.map((productItem) => (
                                      <>
                                        <div className={"flex items-center gap-2"}>
                                          <div className={"text15Semibold text-neutral-1000"}>
                                            {productItem.product.name}
                                          </div>
                                        </div>
                                        <div className={"my-3"}>
                                          {productItem?.product?.type?.internal_type !==
                                            ProductCategoryTypes?.ADD_ONS &&
                                            productItem?.product?.type?.internal_type !==
                                              ProductCategoryTypes?.MERCHANDIZE &&
                                            productItem?.product?.type?.internal_type !==
                                              ProductCategoryTypes?.GIFT_CARD &&
                                            productItem?.product?.type?.internal_type !==
                                              ProductCategoryTypes?.MEMBERSHIP && (
                                              <div className={"flex items-center mt-2"}>
                                                <CalendarIcon
                                                  className={
                                                    "mr-2 [&>path]:stroke-neutral-150 h-4 w-4"
                                                  }
                                                />
                                                <div
                                                  className={
                                                    "lg:text15Regular text14Regular w-full text-neutral-900 truncate"
                                                  }
                                                >
                                                  {productItem?.quantities?.[0].unit?.is_multi_day
                                                    ? `
                                                ${moment(
                                                  productItem.product_availability_slot
                                                    ?.local_start_time
                                                ).format(
                                                  phpToMoment(domainDetails?.date_format || "Y-m-d")
                                                )} (${moment(
                                                  productItem.product_availability_slot
                                                    ?.local_start_time
                                                ).format("hh:mmA")}) - ${moment(
                                                  productItem.product_availability_slot
                                                    ?.local_end_time
                                                ).format(
                                                  phpToMoment(domainDetails?.date_format || "Y-m-d")
                                                )} (${
                                                  productItem.product_availability_slot
                                                    ?.local_end_time
                                                    ? moment(
                                                        productItem.product_availability_slot
                                                          ?.local_end_time
                                                      ).format("hh:mmA")
                                                    : ""
                                                })`
                                                    : productItem.product_availability_slot &&
                                                      moment(
                                                        productItem.product_availability_slot
                                                          ?.local_start_time
                                                      ).format(
                                                        phpToMoment(
                                                          domainDetails?.date_format || "Y-m-d"
                                                        )
                                                      )}
                                                </div>
                                              </div>
                                            )}
                                          {productItem?.product_availability_slot
                                            ?.local_start_time &&
                                            productItem?.product_availability_slot
                                              ?.local_end_time &&
                                            !productItem?.quantities?.[0].unit?.is_multi_day && (
                                              <div className={"flex items-center mt-2"}>
                                                <TimeIcon
                                                  className={"mr-2 text-neutral-150 h-4 w-4"}
                                                />
                                                <div
                                                  className={
                                                    "lg:text15Regular text14Regular w-60 text-neutral-900"
                                                  }
                                                >
                                                  {" "}
                                                  {`${moment(
                                                    productItem?.product_availability_slot
                                                      ?.local_start_time
                                                  ).format("h:mma")} - ${moment(
                                                    productItem?.product_availability_slot
                                                      ?.local_end_time
                                                  ).format("h:mma")}`}
                                                </div>
                                              </div>
                                            )}
                                          {productItem?.quantities?.[0].unit?.is_multi_day ? (
                                            <div className={"flex items-center mt-2"}>
                                              <BillIcon className={"mr-2 text-neutral-400"} />
                                              <div
                                                className={
                                                  "lg:text14Regular text12Regular w-60 text-neutral-900"
                                                }
                                              >
                                                {" "}
                                                {handleShowMultiDay(
                                                  productItem?.product_availability_slot
                                                    ?.local_start_time,
                                                  productItem?.product_availability_slot
                                                    ?.local_end_time
                                                )}
                                              </div>
                                            </div>
                                          ) : null}
                                        </div>
                                        {productItem?.product?.type?.internal_type !==
                                        (ProductCategoryTypes?.MEMBERSHIP as any) ? (
                                          <div key={Math.random()}>
                                            {productItem?.quantities?.map((quantityItem, index) => (
                                              <div key={index} className={"my-2"}>
                                                <div
                                                  className={
                                                    "text14Medium text-neutral-1000 mb-1.5"
                                                  }
                                                >
                                                  {capitalizeFirstLetter(
                                                    quantityItem?.unit?.name
                                                      ? quantityItem?.unit?.name
                                                      : "".toLocaleLowerCase()
                                                  )}{" "}
                                                  <span className={"text14Medium text-orange-800"}>
                                                    {productItem?.product?.type?.internal_type ===
                                                      ProductCategoryTypes?.ADD_ONS && "(Add-ons)"}
                                                  </span>
                                                </div>
                                                <div
                                                  className={"flex items-center justify-between"}
                                                >
                                                  <div className={"text14Regular text-neutral-900"}>
                                                    {" "}
                                                    ({quantityItem.formatted_price}x
                                                    {quantityItem.quantity})
                                                  </div>
                                                  <div className={"text14Medium text-neutral-1000"}>
                                                    {" "}
                                                    {quantityItem.formatted_total}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : null}
                                        {productItem?.product?.type?.internal_type ===
                                        (ProductCategoryTypes?.MEMBERSHIP as any) ? (
                                          <div key={`price-of-membership-${Math.random()}`}>
                                            <div className={"flex justify-between"}>
                                              {productItem?.quantities?.map(
                                                (quantityItem, index) => (
                                                  <div
                                                    key={index}
                                                    className={
                                                      "w-full flex items-center justify-between"
                                                    }
                                                  >
                                                    <div
                                                      className={"text15Regular text-neutral-1000"}
                                                    >
                                                      Price
                                                    </div>
                                                    <div
                                                      className={"text15Regular text-neutral-1000"}
                                                    >
                                                      {quantityItem.formatted_total}
                                                    </div>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                            <div
                                              className={
                                                "flex items-center justify-between mt-2 gap-3"
                                              }
                                            >
                                              <div className={"text15Regular text-neutral-1000"}>
                                                Duration
                                              </div>
                                              <div
                                                className={"text14Medium text-neutral-900 truncate"}
                                              >
                                                {productItem?.product?.membership?.validity
                                                  ?.expiry_type === "fixed_date" ? (
                                                  ` ${moment(
                                                    productItem?.product?.membership?.validity
                                                      ?.start_at,
                                                    "YYYY-MM-DD"
                                                  ).format(
                                                    phpToMoment(
                                                      domainDetails?.date_format || "Y-m-d"
                                                    )
                                                  )} - ${moment(
                                                    productItem?.product?.membership?.validity
                                                      ?.end_at,
                                                    "YYYY-MM-DD"
                                                  ).format(
                                                    phpToMoment(
                                                      domainDetails?.date_format || "Y-m-d"
                                                    )
                                                  )}`
                                                ) : productItem?.product?.membership?.validity
                                                    ?.expiry_type === "fixed_days" ? (
                                                  <div className={"w-full truncate"}>
                                                    <span
                                                      className={"text-neutral-1000 text14Semibold"}
                                                    >
                                                      Expiry:
                                                    </span>{" "}
                                                    {
                                                      productItem?.product?.membership?.validity
                                                        ?.expiry_value
                                                    }{" "}
                                                    {productItem?.product?.membership?.validity
                                                      ?.expiry_value > 1
                                                      ? "days"
                                                      : "day"}{" "}
                                                    from the date of purchase.
                                                  </div>
                                                ) : (
                                                  "Never Expire"
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ) : null}
                                        <div
                                          className={
                                            "w-full my-3 border-t border-solid border-neutral-450"
                                          }
                                        />
                                      </>
                                    ))}
                                  </>
                                )}
                                <div>
                                  <div className={"flex justify-between"}>
                                    <div className={"text15Regular text-neutral-900"}>
                                      Sub Total
                                    </div>
                                    <div className={"text15Regular text-neutral-900"}>
                                      {cartData && cartData.formatted_subtotal}
                                    </div>
                                  </div>

                                  {cartData?.membership_discount > 0 ? (
                                    <div className={"flex items-center justify-between mt-2.5"}>
                                      <div
                                        className={
                                          "text-green-800 text15Medium flex items-center gap-1"
                                        }
                                      >
                                        Membership Discount
                                        <Tooltip
                                          content={
                                            <div className={"min-w-[150px]"}>
                                              <div className={"text-start"}>
                                                <div className={"text14Medium"}>
                                                  {cartData?.customerMembership
                                                    ? `${cartData?.customerMembership?.product?.name}`
                                                    : ``}
                                                </div>
                                                <div className={"text12Regular mt-1"}>
                                                  {cartData?.customerMembership
                                                    ? `${cartData?.customerMembership?.code}`
                                                    : ``}
                                                </div>
                                                <div
                                                  className={
                                                    "w-full my-3 border-t border-solid border-neutral-450"
                                                  }
                                                  key={`border-of-section-${Math.random()}`}
                                                />
                                              </div>
                                              <div className={"text-left flex flex-col gap-1.5"}>
                                                {cartData?.items.map((cartItem) => {
                                                  return (
                                                    <div
                                                      key={cartItem.id}
                                                      className={
                                                        "flex justify-between gap-10 text14Regular"
                                                      }
                                                    >
                                                      <p>
                                                        {cartItem.product.name}
                                                        {`${
                                                          cartItem.membership_discount_json
                                                            ?.amount_type === "percentage"
                                                            ? `(${cartItem.membership_discount_json.amount}%)`
                                                            : ""
                                                        }`}
                                                      </p>
                                                      <p>{`- ${cartItem.formatted_membership_discount}`}</p>
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            </div>
                                          }
                                          placement={"top"}
                                          trigger={"hover"}
                                          className={"text-center"}
                                        >
                                          <InfoIcon className={"mt-1"} />
                                        </Tooltip>
                                      </div>
                                      <div className={"text15Regular flex items-center gap-2"}>
                                        {`- ${cartData && cartData.formatted_membership_discount}`}
                                        {membershipLoading ? (
                                          <Spinner size={"sm"} />
                                        ) : (
                                          <Tooltip
                                            content={"Remove Membership"}
                                            placement={"top"}
                                            className={"text-center"}
                                          >
                                            <CloseCircleIcon
                                              width={16}
                                              height={16}
                                              className={"cursor-pointer text-neutral-500"}
                                              onClick={async () => {
                                                try {
                                                  setMembershipLoading(true)
                                                  if (cartData?.id) {
                                                    await OrderServices.removeMembershipFromCart(
                                                      cartData?.id,
                                                      isIntegratedPage
                                                    )
                                                    refetchCartData()
                                                    membershipFormRef?.current?.OnClearMemberValue()
                                                  }
                                                } catch (e) {
                                                  Sentry.captureException(e)
                                                  console.error("Remove membership: ", e)
                                                } finally {
                                                  setMembershipLoading(false)
                                                }
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}

                                  <div className={"mt-2.5 flex items-center justify-between"}>
                                    <span
                                      className={
                                        "text15Regular text-neutral-900 flex flex-row items-center gap-1"
                                      }
                                    >
                                      <span>Taxes & Fees:</span>
                                      {!!cartData?.levies.length &&
                                        domainDetails?.show_levy_breakdown_in_widget && (
                                          <Tooltip
                                            content={
                                              <div className={"min-w-[150px]"}>
                                                <div className={"text-left"}>
                                                  {cartData?.levies.map((levy) => {
                                                    return (
                                                      <div
                                                        key={levy.id}
                                                        className={"flex justify-between"}
                                                      >
                                                        <p>{levy.name}</p>
                                                        <p>{levy.formatted_amount}</p>
                                                      </div>
                                                    )
                                                  })}
                                                  <hr className={"my-1"} />
                                                  <div className={"flex justify-between mt-1"}>
                                                    <p>Total Taxes & Fees</p>
                                                    <p>{cartData?.formatted_total_levies}</p>
                                                  </div>
                                                </div>
                                              </div>
                                            }
                                            placement={"top"}
                                            className={"text-center"}
                                          >
                                            <span>
                                              <InfoIcon className={"mt-[1px] h-3 w-3"} />
                                            </span>
                                          </Tooltip>
                                        )}
                                    </span>
                                    <span className={"text15Regular text-neutral-900"}>
                                      {`+ ${cartData && cartData.formatted_total_levies}`}
                                    </span>
                                  </div>

                                  {cartData?.tips?.length && cartData?.total_tip ? (
                                    <div className={"mt-2.5 flex items-center justify-between"}>
                                      <div
                                        className={
                                          "text15Regular text-neutral-900 flex items-center gap-1"
                                        }
                                      >
                                        Tip Total:
                                      </div>
                                      <div className={"text15Regular text-neutral-900"}>
                                        +{cartData.total_tip_formatted ?? "0.0"}
                                      </div>
                                    </div>
                                  ) : null}

                                  {cartData?.is_deposit_enabled_for_items ? (
                                    <div className={"mt-2.5 flex items-center justify-between"}>
                                      <div
                                        className={
                                          "text-green-800 text14Regular flex items-center gap-1"
                                        }
                                      >
                                        Deposit
                                        <Tooltip
                                          content={
                                            <div className={"min-w-[150px]"}>
                                              <div className={"text-left flex flex-col gap-1.5"}>
                                                {cartData?.items
                                                  ?.filter((item) => item?.deposit)
                                                  .map((cartItem) => {
                                                    return (
                                                      <div
                                                        key={cartItem.id}
                                                        className={
                                                          "flex justify-between gap-10 text14Regular"
                                                        }
                                                      >
                                                        <p>{cartItem.product.name}</p>
                                                        <p>{cartItem.deposit?.formatted_amount}</p>
                                                      </div>
                                                    )
                                                  })}
                                                <div
                                                  className={
                                                    "w-full border-t border-solid border-neutral-450"
                                                  }
                                                  key={`border-of-section-${Math.random()}`}
                                                />
                                                <div
                                                  className={
                                                    "flex justify-between gap-10 text14Regular"
                                                  }
                                                >
                                                  <p>Total Deposit</p>
                                                  <p>{cartData.formatted_deposit}</p>
                                                </div>
                                              </div>
                                            </div>
                                          }
                                          placement={"top"}
                                          trigger={"hover"}
                                          className={"text-center"}
                                        >
                                          <InfoIcon />
                                        </Tooltip>
                                      </div>
                                      <div className={"text14Medium"}>{`${
                                        cartData && cartData.formatted_deposit
                                      }`}</div>
                                    </div>
                                  ) : null}

                                  {/* Promocode/Vouchers/Giftcards */}
                                  {!(cartData?.total_with_tip === 0 && !cartData?.offer_code) ? (
                                    <div className={"mt-2"}>
                                      {values.showPromoCode ? (
                                        isPromoCodeValidated ? (
                                          <div
                                            className={
                                              "flex flex-row items-center justify-between gap-4"
                                            }
                                          >
                                            <div
                                              className={
                                                "flex flex-row items-center justify-start gap-2"
                                              }
                                            >
                                              <ValidPromoCodeIcon className={"shrink-0"} />
                                              <span
                                                className={
                                                  "text-green-700 text12Semibold break-all"
                                                }
                                              >
                                                {giftcardDetails ? "Gift card" : "Promocode"}{" "}
                                                Applied -{" "}
                                                <span className={"uppercase"}>
                                                  {values.promoCode}
                                                </span>
                                              </span>
                                              <CloseCircleIcon
                                                width={16}
                                                height={16}
                                                className={
                                                  "cursor-pointer text-neutral-500 shrink-0"
                                                }
                                                onClick={async () => {
                                                  try {
                                                    if (cartData?.id) {
                                                      setIsOverlayLoader(true)
                                                      await OrderServices.removePromocode(
                                                        cartData?.id,
                                                        isIntegratedPage
                                                      )
                                                      refetchCartData()
                                                      setFieldValue("promoCode", "")
                                                      setFieldValue("showPromoCode", false)
                                                      setFieldTouched("promoCode", false)
                                                      setIsPromocodeValidated(false)
                                                      setGiftcardDetails(undefined)
                                                    }
                                                  } catch (e) {
                                                    Sentry.captureException(e)
                                                    console.error("Remove promocode: ", e)
                                                  } finally {
                                                    setIsOverlayLoader(false)
                                                  }
                                                }}
                                              />
                                            </div>
                                            <div
                                              className={
                                                "flex flex-row items-center justify-end gap-1"
                                              }
                                            >
                                              <span className={"text14Medium text-neutral-900"}>
                                                -
                                                {giftcardDetails
                                                  ? values.paymentType ===
                                                    PaymentTypes.DEPOSIT_AMOUNT
                                                    ? giftcardDetails?.deposit_total
                                                        ?.amount_formatted
                                                    : giftcardDetails?.total?.amount_formatted
                                                  : cartData && cartData?.formatted_discount}
                                              </span>
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className={
                                              "flex flex-row items-baseline justify-between gap-1"
                                            }
                                          >
                                            <FormGroup
                                              name={"promoCode"}
                                              className={"mr-3"}
                                              errorClassName={"max-w-[195px]"}
                                            >
                                              <TextInput
                                                {...getFieldProps("promoCode")}
                                                placeholder={"Enter Code"}
                                                color={
                                                  errors.promoCode && touched.promoCode
                                                    ? "failure"
                                                    : undefined
                                                }
                                                className={"max-w-[195px]"}
                                                autoComplete={"off"}
                                              />
                                            </FormGroup>
                                            <div className={"flex items-center gap-1"}>
                                              <Button
                                                size={"xs"}
                                                color={"primary"}
                                                disabled={!values.promoCode}
                                                onClick={() =>
                                                  handleApplyPromoCode(
                                                    values.promoCode,
                                                    values.paymentType
                                                  )
                                                }
                                              >
                                                Apply
                                              </Button>
                                              <Button
                                                color={"ghost"}
                                                size={"xs"}
                                                onClick={() => {
                                                  setFieldValue("promoCode", "")
                                                  setFieldValue("showPromoCode", false)
                                                  setFieldTouched("promoCode", false)
                                                }}
                                              >
                                                {" "}
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                        )
                                      ) : (
                                        <button
                                          type={"button"}
                                          className={`flex flex-row items-center h-6 bg-transparent border-none`}
                                          onClick={() =>
                                            setFieldValue("showPromoCode", !values.showPromoCode)
                                          }
                                        >
                                          <AddModeIcon className={"mr-2 text-primary-600"} />
                                          <span className={"text14Medium text-primary-600"}>
                                            Add Promo/Voucher/Gift Card
                                          </span>
                                          {!(
                                            cartData?.customerMembership?.product?.membership
                                              ?.limitation?.can_combine_with_offers ?? true
                                          ) ? (
                                            <Tooltip
                                              content={`${cartData.customerMembership.product.name} can not be combined with Promocode & Voucher.`}
                                              placement={"top"}
                                              className={"text-center"}
                                            >
                                              <InfoIcon className={"ml-1 text-primary-600"} />
                                            </Tooltip>
                                          ) : null}
                                        </button>
                                      )}
                                    </div>
                                  ) : null}

                                  {/* Affiliate code */}
                                  {domainDetails?.enable_affiliate ? (
                                    <div className={"mt-2"}>
                                      {values.showAgentCode ? (
                                        isAgentCodeValidated ? (
                                          <div
                                            className={
                                              "flex flex-row items-center justify-between gap-4"
                                            }
                                          >
                                            <div
                                              className={
                                                "flex flex-row items-center justify-start gap-2"
                                              }
                                            >
                                              <ValidPromoCodeIcon />
                                              <span className={"text-green-700 text12Semibold"}>
                                                Agentcode Applied -{" "}
                                                <span className={"uppercase"}>
                                                  {values.agentCode}
                                                </span>
                                              </span>
                                            </div>
                                            <div
                                              className={
                                                "flex flex-row items-center justify-end gap-1"
                                              }
                                            >
                                              <CloseCircleIcon
                                                width={16}
                                                height={16}
                                                className={"cursor-pointer text-neutral-500"}
                                                onClick={async () => {
                                                  try {
                                                    if (cartData?.id) {
                                                      setIsOverlayLoader(true)
                                                      await OrderServices.updateCart(
                                                        cartId,
                                                        {
                                                          ...getCartUpdatePayload(),
                                                          affiliate_id: "",
                                                        },
                                                        isIntegratedPage,
                                                        {
                                                          headers: {
                                                            "X-Channel": UserChannel.AFFILIATE,
                                                          },
                                                        }
                                                      )
                                                      refetchCartData()
                                                      setFieldValue("agentCode", "")
                                                      setFieldValue("showAgentCode", false)
                                                      setFieldTouched("agentCode", false)
                                                      setIsAgentcodeValidated(false)
                                                    }
                                                  } catch (e) {
                                                    Sentry.captureException(e)
                                                    console.error("Remove promocode: ", e)
                                                  } finally {
                                                    setIsOverlayLoader?.(false)
                                                  }
                                                }}
                                              />
                                            </div>
                                          </div>
                                        ) : (
                                          <div
                                            className={
                                              "flex flex-row items-baseline justify-between gap-1"
                                            }
                                          >
                                            <FormGroup
                                              name={"agentCode"}
                                              className={"mr-3"}
                                              errorClassName={"max-w-[195px]"}
                                            >
                                              <TextInput
                                                {...getFieldProps("agentCode")}
                                                placeholder={"Enter Code"}
                                                color={
                                                  errors.agentCode && touched.agentCode
                                                    ? "failure"
                                                    : undefined
                                                }
                                                className={"max-w-[195px]"}
                                                autoComplete={"off"}
                                                readOnly={Boolean(cartData?.affiliate)}
                                              />
                                            </FormGroup>
                                            <div className={"flex items-center gap-1"}>
                                              <Button
                                                size={"xs"}
                                                color={"primary"}
                                                disabled={Boolean(
                                                  cartData?.affiliate || !values?.agentCode
                                                )}
                                                onClick={() =>
                                                  handleApplyAgentCode(
                                                    values.agentCode,
                                                    setFieldError
                                                  )
                                                }
                                              >
                                                Apply
                                              </Button>
                                              <Button
                                                color={"ghost"}
                                                size={"xs"}
                                                onClick={async () => {
                                                  try {
                                                    setFieldValue("agentCode", "")
                                                    setFieldValue("showAgentCode", false)
                                                    setFieldTouched("agentCode", false)
                                                    setIsAgentcodeValidated(false)
                                                  } catch (e) {
                                                    Sentry.captureException(e)
                                                    console.error("Remove promocode: ", e)
                                                  }
                                                }}
                                              >
                                                Cancel
                                              </Button>
                                            </div>
                                          </div>
                                        )
                                      ) : (
                                        <button
                                          type={"button"}
                                          className={
                                            "flex flex-row items-center h-6 bg-transparent border-none"
                                          }
                                          onClick={() =>
                                            setFieldValue("showAgentCode", !values.showAgentCode)
                                          }
                                        >
                                          <AddModeIcon className={"mr-2 text-primary-600"} />
                                          <span className={"text14Medium text-primary-600"}>
                                            Add Affiliate Code
                                          </span>
                                        </button>
                                      )}
                                    </div>
                                  ) : null}

                                  {/* Price Breakup */}
                                  <div
                                    className={
                                      "w-full my-4 border-t border-dashed border-neutral-300"
                                    }
                                  />
                                  {showPaymentOptions ? (
                                    <>
                                      <div className={"md:hidden"}>
                                        <div className={"text14Regular text-neutral-900"}>
                                          Amount to pay now
                                        </div>
                                        <div
                                          className={
                                            "flex flex-row items-center justify-start gap-5 mt-2"
                                          }
                                        >
                                          {PAYMENT_TYPES.map((paymentType) => {
                                            return (
                                              <div
                                                className={"flex items-center gap-2"}
                                                key={paymentType.value}
                                              >
                                                <Radio
                                                  id={`${paymentType.value}-2`}
                                                  name={"paymentType-2"}
                                                  value={paymentType.value}
                                                  checked={Boolean(
                                                    values?.paymentType === paymentType.value
                                                  )}
                                                  onChange={() => {
                                                    setFieldValue("paymentType", paymentType.value)
                                                    handleApplyPromoCode(
                                                      values.promoCode,
                                                      paymentType.value
                                                    )
                                                    if (
                                                      paymentType.value ===
                                                      PaymentTypes.DEPOSIT_AMOUNT
                                                    ) {
                                                      setFieldValue(
                                                        "amount",
                                                        cartData?.deposit_pay_now?.amount_in_units
                                                      )
                                                    } else if (
                                                      paymentType.value === PaymentTypes.FULL_AMOUNT
                                                    ) {
                                                      setFieldValue(
                                                        "amount",
                                                        cartData?.full_amount_pay_now
                                                          ?.amount_in_units ??
                                                          cartData?.total_with_tip
                                                      )
                                                    } else {
                                                      setFieldValue("amount", 0)
                                                    }

                                                    const integrationName =
                                                      integratedData &&
                                                      integratedData[values.activeTab]?.name
                                                    if (integrationName === "Stripe") {
                                                      if (
                                                        stripeCreditCardFormRef?.current
                                                          ?.setFieldValue
                                                      ) {
                                                        stripeCreditCardFormRef.current.setFieldValue(
                                                          "paymentType",
                                                          paymentType.value
                                                        )
                                                        if (
                                                          paymentType.value ===
                                                          PaymentTypes.DEPOSIT_AMOUNT
                                                        ) {
                                                          stripeCreditCardFormRef.current.setFieldValue(
                                                            "amount",
                                                            `${cartData?.deposit_pay_now?.amount_in_units}`
                                                          )
                                                        } else if (
                                                          paymentType.value ===
                                                          PaymentTypes.FULL_AMOUNT
                                                        ) {
                                                          stripeCreditCardFormRef.current.setFieldValue(
                                                            "amount",
                                                            `${cartData?.full_amount_pay_now?.amount_in_units ?? cartData?.total_with_tip}`
                                                          )
                                                        } else {
                                                          stripeCreditCardFormRef.current.setFieldValue(
                                                            "amount",
                                                            0
                                                          )
                                                        }
                                                      }
                                                    } else if (integrationName === "SquareUp") {
                                                      if (squareUpFormRef?.current?.setFieldValue) {
                                                        squareUpFormRef.current.setFieldValue(
                                                          "paymentType",
                                                          paymentType.value
                                                        )
                                                        if (
                                                          paymentType.value ===
                                                          PaymentTypes.DEPOSIT_AMOUNT
                                                        ) {
                                                          squareUpFormRef.current.setFieldValue(
                                                            "amount",
                                                            `${cartData?.deposit_pay_now?.amount_in_units}`
                                                          )
                                                        } else if (
                                                          paymentType.value ===
                                                          PaymentTypes.FULL_AMOUNT
                                                        ) {
                                                          squareUpFormRef.current.setFieldValue(
                                                            "amount",
                                                            `${cartData?.full_amount_pay_now?.amount_in_units ?? cartData?.total_with_tip}`
                                                          )
                                                        } else {
                                                          squareUpFormRef.current.setFieldValue(
                                                            "amount",
                                                            0
                                                          )
                                                        }
                                                      }
                                                    }
                                                  }}
                                                />
                                                <Label
                                                  htmlFor={`${paymentType.value}-2`}
                                                  className={"text12Medium text-neutral-900"}
                                                >
                                                  {paymentType.label}
                                                </Label>
                                              </div>
                                            )
                                          })}
                                        </div>
                                      </div>

                                      <div className={"mt-2.5 flex justify-between"}>
                                        <div className={"text16Semibold"}>
                                          {giftcardDetails ? "Total Payable" : "Total"}
                                        </div>
                                        <div className={"text16Semibold"}>
                                          {giftcardDetails
                                            ? values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                              ? giftcardDetails?.remaining_deposit_total
                                                  .amount_formatted
                                              : giftcardDetails?.remaining_total.amount_formatted
                                            : cartData && cartData.formatted_total_with_tip}
                                        </div>
                                      </div>

                                      {(
                                        values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                          ? cartData?.deposit_pay_later?.amount_in_units ||
                                            cartData?.is_deposit_enabled_for_items
                                          : cartData?.full_amount_pay_later?.amount_in_units
                                      ) ? (
                                        <>
                                          <div className={"flex justify-between mt-3"}>
                                            <div className={"text14Regular flex items-center"}>
                                              Pay later
                                              <Tooltip
                                                content={
                                                  <div className={"w-[230px]"}>
                                                    <div className={"text-center text14Regular"}>
                                                      {cartData?.has_pending_booking
                                                        ? "This amount will be charged once your booking is confirmed for the respective products."
                                                        : "This amount is the balance due from the total amount and will be collected later."}
                                                    </div>
                                                  </div>
                                                }
                                                placement={"top"}
                                                trigger={"hover"}
                                                className={"text-center"}
                                              >
                                                <InfoIcon className={"mt-0.5 ml-1"} />
                                              </Tooltip>
                                            </div>
                                            <div className={"text14Regular"}>
                                              {`- ${values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT && !cartData?.has_pending_booking ? cartData?.deposit_pay_later?.amount_formatted : cartData?.full_amount_pay_later?.amount_formatted}`}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              "w-full my-3.5 border-t border-dashed border-neutral-300"
                                            }
                                          />

                                          <div className={"flex justify-between"}>
                                            <div className={"text16Semibold"}>Pay Now</div>
                                            <div className={"text16Semibold"}>
                                              {values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                                ? cartData.deposit_pay_now.amount_formatted
                                                : cartData.full_amount_pay_now.amount_formatted}
                                            </div>
                                          </div>
                                        </>
                                      ) : null}

                                      {Boolean(giftcardDetails) && (
                                        <div
                                          className={`flex flex-row items-center justify-between gap-2 p-3 border-y mt-4 border-beige-400 bg-beige-200`}
                                        >
                                          <div
                                            className={
                                              "flex flex-row items-center gap-2 justify-start"
                                            }
                                          >
                                            <InfoIcon className={"shrink-0"} />
                                            <p className={"text14Medium text-neutral-1000"}>
                                              {values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                                ? giftcardDetails?.remaining_deposit_total
                                                    .amount === 0
                                                  ? giftcardDetails.remaining_deposit_balance
                                                      .amount > 0
                                                    ? `The gift card covers the entire deposit amount and the new giftcard balance will be ${giftcardDetails?.remaining_deposit_balance.amount_formatted}. Click Submit to place the order.`
                                                    : `The gift card covers the entire amount. Continue to place order.`
                                                  : `The gift card is applied! Please use other payment methods for the remaining ${giftcardDetails?.remaining_deposit_total.amount_formatted}`
                                                : giftcardDetails?.remaining_total.amount === 0
                                                  ? giftcardDetails.remaining_balance.amount > 0
                                                    ? `The gift card covers the entire order amount and the new giftcard balance will be ${giftcardDetails?.remaining_balance.amount_formatted}. Click Submit to place the order.`
                                                    : `The gift card covers the entire amount. Continue to place order.`
                                                  : `The gift card is applied! Please use other payment methods for the remaining ${giftcardDetails?.remaining_total.amount_formatted}`}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <div className={"flex justify-between"}>
                                        {giftcardDetails ? (
                                          <>
                                            <div className={"text16Regular"}>Total Payable</div>
                                            <div className={"text16Semibold"}>
                                              {values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                                ? giftcardDetails?.remaining_deposit_total
                                                    .amount_formatted
                                                : giftcardDetails?.remaining_total.amount_formatted}
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className={"text16Medium"}>Total</div>
                                            <div className={"text16Medium"}>
                                              {cartData && cartData.formatted_total_with_tip}
                                            </div>
                                          </>
                                        )}
                                      </div>
                                      {(
                                        values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                          ? cartData?.deposit_pay_later?.amount_in_units ||
                                            cartData?.is_deposit_enabled_for_items
                                          : cartData?.full_amount_pay_later?.amount_in_units
                                      ) ? (
                                        <>
                                          <div className={"flex justify-between mt-3"}>
                                            <div className={"text14Regular flex items-center"}>
                                              Pay later
                                              <Tooltip
                                                content={
                                                  <div className={"w-[230px]"}>
                                                    <div className={"text-center text14Regular"}>
                                                      {cartData?.has_pending_booking
                                                        ? "This amount will be charged once your booking is confirmed for the respective products."
                                                        : "This amount is the balance due from the total amount and will be collected later."}
                                                    </div>
                                                  </div>
                                                }
                                                placement={"top"}
                                                trigger={"hover"}
                                                className={"text-center"}
                                              >
                                                <InfoIcon className={"mt-0.5 ml-1"} />
                                              </Tooltip>
                                            </div>
                                            <div className={"text14Regular"}>
                                              {`- ${values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT && !cartData?.has_pending_booking ? cartData?.deposit_pay_later?.amount_formatted : cartData?.full_amount_pay_later?.amount_formatted}`}
                                            </div>
                                          </div>
                                          <div
                                            className={
                                              "w-full my-3.5 border-t border-dashed border-neutral-300"
                                            }
                                          />
                                          <div className={"flex justify-between"}>
                                            <div className={"text16Semibold"}>Pay Now</div>
                                            <div className={"text16Semibold"}>
                                              {values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                                ? cartData.deposit_pay_now.amount_formatted
                                                : cartData.full_amount_pay_now.amount_formatted}
                                            </div>
                                          </div>
                                        </>
                                      ) : null}

                                      {giftcardDetails ? (
                                        <div
                                          className={`flex flex-row items-center justify-between gap-2 p-3 border-y mt-4 border-beige-400 bg-beige-200`}
                                        >
                                          <div
                                            className={
                                              "flex flex-row items-center gap-2 justify-start"
                                            }
                                          >
                                            <InfoIcon className={"shrink-0"} />
                                            <p className={"text14Medium text-neutral-1000"}>
                                              {(values.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                                ? giftcardDetails?.remaining_deposit_total.amount
                                                : giftcardDetails?.remaining_total.amount) === 0
                                                ? (values.paymentType ===
                                                  PaymentTypes.DEPOSIT_AMOUNT
                                                    ? giftcardDetails.remaining_deposit_balance
                                                        .amount
                                                    : giftcardDetails.remaining_balance.amount) > 0
                                                  ? `The gift card covers the entire order amount and the new giftcard balance will be ${
                                                      values.paymentType ===
                                                      PaymentTypes.DEPOSIT_AMOUNT
                                                        ? giftcardDetails?.remaining_deposit_balance
                                                            .amount_formatted
                                                        : giftcardDetails?.remaining_balance
                                                            .amount_formatted
                                                    }. Click Submit to place the order.`
                                                  : `The gift card covers the entire amount. Continue to place order.`
                                                : `The gift card is applied! Please use other payment methods for the remaining ${
                                                    values.paymentType ===
                                                    PaymentTypes.DEPOSIT_AMOUNT
                                                      ? giftcardDetails?.remaining_deposit_total
                                                          .amount_formatted
                                                      : giftcardDetails?.remaining_total
                                                          .amount_formatted
                                                  }`}
                                            </p>
                                          </div>
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                                <div className={"flex mt-4 md:mt-0 md:hidden"}>
                                  <Checkbox
                                    id={"agreeTermsAndConditions"}
                                    checked={values.agreeTermsAndConditions}
                                    {...getFieldProps("agreeTermsAndConditions")}
                                    name={"agreeTermsAndConditions"}
                                    className={"cursor-pointer text-primary-600 mt-1"}
                                  />
                                  <div className={"mx-2 leading-3"}>
                                    <Label
                                      htmlFor={"agreeTermsAndConditions"}
                                      className={"sm:!paragraph14Regular !paragraph14Regular"}
                                    >
                                      I agree to all these&nbsp;
                                      <Link
                                        target={"_blank"}
                                        className={"cursor-pointer text-primary-600"}
                                        href={routes.TnC}
                                      >
                                        Terms & Conditions
                                      </Link>
                                      &nbsp;and your&nbsp;
                                      <Link
                                        target={"_blank"}
                                        className={"cursor-pointer text-primary-600"}
                                        href={routes.privacy}
                                      >
                                        Privacy Policy
                                      </Link>
                                      .
                                    </Label>
                                  </div>
                                </div>
                                <Button
                                  disabled={
                                    values.isLoading ||
                                    isSubmitting ||
                                    !values.agreeTermsAndConditions ||
                                    (integratedData.length === 0 &&
                                      !isPlaceOrder &&
                                      (cartData?.total_with_tip > 0 ||
                                        Boolean(cartData?.deposit) ||
                                        !cartData?.is_deposit_enabled_for_items))
                                  }
                                  type={"submit"}
                                  color={"primary"}
                                  data-testid={"FullAmountPayButton"}
                                  className={
                                    "w-full mt-3 md:hidden [&>span]:!text16Medium [&>span]:!py-[13px]"
                                  }
                                >
                                  {isSubmitting || values.isLoading ? (
                                    <Spinner
                                      size={"sm"}
                                      light
                                      className={"text-primary-400 fill-white"}
                                    />
                                  ) : giftcardDetails ? (
                                    (values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                      ? giftcardDetails?.remaining_deposit_total.amount
                                      : giftcardDetails?.remaining_total.amount) === 0 ? (
                                      `Place order`
                                    ) : (
                                      `Pay ${
                                        values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                          ? giftcardDetails?.remaining_deposit_total
                                              .amount_formatted
                                          : giftcardDetails?.remaining_total.amount_formatted
                                      }`
                                    )
                                  ) : (cartData?.total_with_tip === 0 ||
                                      cartData?.full_amount_pay_now?.amount_in_units === 0 ||
                                      (cartData?.is_deposit_enabled_for_items &&
                                        values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT &&
                                        !(cartData?.deposit_with_tip > 0))) &&
                                    (Number(values?.amount) === 0 || cartData?.deposit === 0) ? (
                                    "Place order"
                                  ) : (
                                    `Pay ${
                                      values?.paymentType === PaymentTypes.DEPOSIT_AMOUNT
                                        ? cartData?.deposit_pay_now?.amount_formatted
                                        : cartData?.full_amount_pay_now?.amount_formatted
                                    }`
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {showPaymentConfirmationModal ? (
                    <PaymentConfirmationModal
                      setShowPaymentConfirmation={setShowPaymentConfirmation}
                      pendingProducts={getPendingConfirmProducts}
                      paylaterAmount={cartData?.full_amount_pay_later?.amount_formatted}
                      show={showPaymentConfirmationModal}
                      handleContinueOrder={() => handleSubmit()}
                      handleClose={() => {
                        setShowPaymentConfirmationModal(false)
                        handlePaymentError()
                      }}
                      isLoading={isSubmitting || values.isLoading}
                    />
                  ) : null}
                </Form>
              )
            }}
          </Formik>
        </Modal.Body>
        <Modal.Footer className="sm:px-6 sm:py-4 px-4 py-3">
          <div
            className={`flex flex-1 flex-row items-center justify-between border-neutral-200 bg-whiteBlack-100`}
          >
            <div className={"flex items-center justify-between w-full"}>
              <div className={"flex flex-col"}>
                <div className={"paragraph13 text-neutral-1000"}>
                  Powered by{" "}
                  <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                    <span className={"text-[#2F69FE] font-Medium"}>{`${widgetsDomainName}`}</span>
                  </a>
                </div>
                <div
                  className={"text12Medium text-neutral-600"}
                >{`All prices are in ${domainDetails?.currency_iso3}`}</div>
              </div>
              <div className={"mr-2 w-fit"}>
                <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                  <BookingSwLogo />
                </a>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      <SelectProductModal
        show={showProductModal}
        setShow={setShowProductModal}
        handleClose={() => {
          setShowProductModal(false)
        }}
        isFromCheckOutModal
        refetchCartData={refetchCartData}
        isIntegratedPage={isIntegratedPage}
        cartItemDetails={getCartUpdatePayload() as unknown as CartItemDetailsType}
        cartParticipantsDetails={
          participantsDetails && cartData
            ? {
                participants: getCartItemsParticipantsPayload(),
              }
            : undefined
        }
        cart_Id={cartId}
        customer_id={cartData?.customer?.id}
        domainDetails={domainDetails}
        refetchParticipantsDetails={refetchParticipantsDetails}
        hideCustomerDetails={hideCustomerDetails}
      />
      {cartData?.customer?.id ? (
        <EditCustomerModal
          show={editUserModal}
          customerData={cartData?.customer}
          handleClose={() => setEditUserModal((prev) => !prev)}
          onSuccess={() => {
            setEditUserModal(false)
            refetchCartData?.()
          }}
          isIntegratedPage={isIntegratedPage}
        />
      ) : null}
    </>
  )
}

export default Checkout
